import React from "react";
import { makeClassName, Row, useUpdatingRef } from "@hex-insights/core";
import { SelectInput, SelectInputProps, TextInput, TextInputProps } from "@hex-insights/forms";

const valueOther = "__other__";

export type SelectTextInputProps = TextInputProps &
	Pick<SelectInputProps<string>, "isLoading" | "options" | "displayOption"> & {
		showInvalid: boolean;
	};

export function SelectTextInput({
	id,
	name,
	value,
	setValue,
	isLoading,
	options,
	onFocus,
	onBlur,
	type,
	inputMode,
	placeholder,
	autoComplete,
	autoFocus,
	disabled,
	allowLeadingWhitespace,
	allowTrailingWhitespace,
	displayOption,
	showInvalid,
	className,
	style,
}: SelectTextInputProps) {
	const [isOtherSelected, setIsOtherSelected] = React.useState(
		() => !options.some((option) => option.value === value) && value !== "",
	);
	const isOtherSelectRef = useUpdatingRef(isOtherSelected);

	const fullOptions = React.useMemo(() => {
		return [...options, { value: valueOther, label: "Other" }];
	}, [options]);

	const selectInputID = id ?? name;
	const textInputID = selectInputID + "--other";
	const focusOtherInput = React.useCallback(() => {
		const input = document.querySelector("#" + textInputID);
		if (!input) {
			return;
		}
		(input as HTMLInputElement).focus();
	}, [textInputID]);

	const setValueFromSelect = React.useCallback(
		(value: string) => {
			if (value === valueOther) {
				if (isOtherSelectRef.current) {
					return;
				}
				setIsOtherSelected(true);
				setValue("");
				setTimeout(() => {
					focusOtherInput();
				}, 0);
				return;
			}
			setIsOtherSelected(false);
			setValue(value);
		},
		[setValue, focusOtherInput, isOtherSelectRef],
	);

	return (
		<Row justify="spaced-start" className={className} style={style}>
			<SelectInput
				id={selectInputID}
				name={name as string}
				value={isOtherSelected ? valueOther : value}
				options={fullOptions}
				isLoading={isLoading}
				setValue={setValueFromSelect}
				onFocus={onFocus}
				onBlur={onBlur}
				autoFocus={autoFocus}
				disabled={disabled}
				displayOption={displayOption}
				inputClassName={makeClassName(
					"select-field__select-input__search-input__WQK8n-1O",
					showInvalid ? "select-field__select-input__search-input--invalid__dgp5X0Ud" : undefined,
				)}
				dropDownClassName="select-field__select-input__drop-down__aEDwpXng"
				optionClassName="select-field__select-input__option__dN5lYl6B"
				className="select-field__select-input__hksUh3cV"
			/>

			<TextInput
				id={textInputID}
				value={isOtherSelected ? value : ""}
				setValue={setValue}
				onFocus={onFocus}
				onBlur={onBlur}
				placeholder={placeholder ?? "Other"}
				type={type}
				inputMode={inputMode}
				autoComplete={autoComplete}
				allowLeadingWhitespace={allowLeadingWhitespace}
				allowTrailingWhitespace={allowTrailingWhitespace}
				disabled={disabled || !isOtherSelected}
				className={makeClassName(
					"text-field__text-input__OmVfyhqJ",
					showInvalid ? "text-field__text-input--invalid__5MeqPuz1" : "",
				)}
			/>
		</Row>
	);
}
